import React, {
  FunctionComponent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useUIDSeed } from 'react-uid';

import { inlineMenuIcon } from '@justgiving/icons';
import SVG from '@justgiving/svg';
import cn from 'classnames';

import { useModalToggle } from '../../hooks/useModalToggle';
import {
  isDownKey,
  isTabNextKey,
  isTabPreviousKey,
  isUpKey,
} from '../../utils/keyboard';
import { IconButton } from '../IconButton/IconButton';

import styles from './DropdownMenu.scss';

export interface DropdownMenuItem {
  key: string | number;
  icon?: string;
  label?: string | JSX.Element;
  onClick?: (event: MouseEvent) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  disabled?: boolean;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  icon?: string | JSX.Element;
  ariaLabel?: string;
}

export const DropdownMenu: FunctionComponent<DropdownMenuProps> = ({
  items,
  ariaLabel,
}) => {
  const {
    setOpen: setIsOpen,
    isOpen,
    ref: modalRef,
    Portal,
  } = useModalToggle();
  const [focusIndex, setFocusIndex] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const count = items.length;
  const lastIndex = count - 1;
  const seed = useUIDSeed();

  const onMenuButtonKeyDown = (event: KeyboardEvent) => {
    if (isDownKey(event) || isUpKey(event)) {
      setIsOpen(true);
      setFocusIndex(isDownKey(event) ? 0 : lastIndex);
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onMenuButtonClick = (event: MouseEvent) => {
    setIsOpen(val => !val);
    setFocusIndex(0);
    event.preventDefault();
    event.stopPropagation();
  };

  const onItemKeyDown = (event: KeyboardEvent) => {
    if (
      (isTabNextKey(event) && focusIndex === lastIndex) ||
      (isTabPreviousKey(event) && focusIndex === 0)
    ) {
      setIsOpen(false);
    } else if (isDownKey(event) || isTabNextKey(event)) {
      setFocusIndex(val => (val !== lastIndex ? val + 1 : 0));
      event.preventDefault();
      event.stopPropagation();
    } else if (isUpKey(event) || isTabPreviousKey(event)) {
      setFocusIndex(val => (val !== 0 ? val - 1 : lastIndex));
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onItemClick = (onClick: (e: MouseEvent) => void) => (e: MouseEvent) => {
    setIsOpen(false);
    onClick(e);
  };

  useEffect(() => {
    if (isOpen) {
      menuItemsRef.current[focusIndex]!.focus();
    }
  }, [isOpen, focusIndex]);

  return (
    <div className={styles.container} ref={containerRef}>
      <IconButton
        aria-controls={seed('menu')}
        aria-expanded={isOpen}
        aria-haspopup={true}
        aria-label={ariaLabel}
        className={cn(styles.button, { [styles.preventClick]: isOpen })}
        id={seed('menubutton')}
        onClick={onMenuButtonClick}
        onKeyDown={onMenuButtonKeyDown}
      >
        <SVG markup={inlineMenuIcon} />
      </IconButton>
      {isOpen && (
        <Portal domNode={containerRef.current}>
          <div className={cn(styles.menu, `jg-shadow-md`)}>
            <svg viewBox="0 0 14 7" className={styles.caret}>
              <path d="M 0 7 L 7 1 L 14 7" fill="#ffffff" stroke="#cccccc" />
            </svg>
            <ul
              ref={modalRef}
              aria-labelledby={seed('menubutton')}
              id={seed('menu')}
              role="menu"
            >
              {items.map(
                ({ onClick = () => {}, icon, label, key, disabled }, i) => (
                  <li role="presentation" key={key}>
                    <button
                      role="menuitem"
                      tabIndex={-1}
                      onClick={onItemClick(onClick)}
                      onKeyDown={onItemKeyDown}
                      className={cn(
                        'jg-btn--invisible',
                        'jt-btn--auto-width',
                        styles.item,
                      )}
                      ref={el => (menuItemsRef.current[i] = el)}
                      disabled={disabled}
                    >
                      {icon && <SVG markup={icon} className={styles.icon} />}
                      <div>{label}</div>
                    </button>
                  </li>
                ),
              )}
            </ul>
          </div>
        </Portal>
      )}
    </div>
  );
};
